/*
 * Which tab a URL belongs to. Pure so the route rules stay unit-testable.
 * Everything under a case is one case tab, the client profile is one client
 * tab, and every other page is a page tab keyed by its sidebar section.
 */
import { matchPath } from "react-router-dom";

import { defaultCrumbsForPath } from "@/components/app-header/routeTitles";
import { CALENDAR_PATH } from "@/features/calendar/routes";
import { parseCasePath } from "@/features/caseIde/routes";
import { COMPANIES_PATH } from "@/features/companies/routes";
import { CONNECTORS_PATH } from "@/features/connectors/routes";
import { HOME_PATH } from "@/features/dashboard/routes";
import { AUTOMATIONS_PATH } from "@/features/automations/routes";
import { FIRM_KNOWLEDGE_PATH } from "@/features/firmKnowledge/routes";
import { ONBOARDING_PATH } from "@/features/onboarding/routes";
import { TIME_PATH, TIME_REVIEW_PATH } from "@/features/timeTracking/routes";
import { TUTORIALS_PATH } from "@/features/tutorials/routes";

import type { TabTarget } from "./types";

/** Sidebar pages, most specific first; each owns everything under its path. */
export const PAGE_TABS: Array<{ id: string; path: string; title: string }> = [
  { id: "home", path: HOME_PATH, title: "Home" },
  { id: "calendar", path: CALENDAR_PATH, title: "Calendar" },
  { id: "billing-review", path: TIME_REVIEW_PATH, title: "Billing Review" },
  { id: "time", path: TIME_PATH, title: "Time" },
  { id: "cases", path: "/cases", title: "Cases" },
  { id: "clients", path: "/clients", title: "Clients" },
  { id: "companies", path: COMPANIES_PATH, title: "Companies" },
  { id: "firm-knowledge", path: FIRM_KNOWLEDGE_PATH, title: "Firm Knowledge" },
  { id: "automations", path: AUTOMATIONS_PATH, title: "Automations" },
  { id: "connectors", path: CONNECTORS_PATH, title: "Connectors" },
  { id: "tutorials", path: TUTORIALS_PATH, title: "Tutorials" },
  { id: "athena", path: "/athena", title: "Athena" },
  { id: "onboarding", path: ONBOARDING_PATH, title: "Get Started" }
];

export const HOME_TAB_ID = "page:home";
export const ONBOARDING_TAB_ID = "page:onboarding";

/**
 * Get Started lives only while onboarding is open: it has no close, and
 * leaving the page drops it.
 */
export const canCloseTab = (id: string): boolean => id !== ONBOARDING_TAB_ID;

/**
 * Page tabs that open as kept tabs. Every other page (Cases, Clients,
 * Connectors, ...) is navigation: it opens as the preview tab, so the next
 * place you go replaces it instead of piling up.
 */
const KEPT_PAGE_TAB_IDS: ReadonlySet<string> = new Set([
  HOME_TAB_ID,
  ONBOARDING_TAB_ID
]);

export const isKeptPageTab = (id: string): boolean => KEPT_PAGE_TAB_IDS.has(id);

export const caseTabId = (individualId: string): string =>
  `case:${individualId}`;
export const clientTabId = (clientId: string): string => `client:${clientId}`;

const under = (pathname: string, root: string): boolean =>
  pathname === root || pathname.startsWith(`${root}/`);

/** The client a client-profile path is about (`/clients/:id`, not cases under it). */
const clientProfileId = (pathname: string): string | null => {
  const match = matchPath({ path: "/clients/:clientId/*" }, pathname);
  const id = (match?.params.clientId ?? "").trim();
  return id && id !== "new" ? id : null;
};

/** The tab a location belongs to. `route` keeps the search so a tab returns to the same view. */
export function tabForPath(pathname: string, search = ""): TabTarget {
  const route = `${pathname}${search}`;
  const casePath = parseCasePath(pathname);
  if (casePath) {
    const clientId = casePath.ref.foreignNationalId?.trim();
    return {
      id: caseTabId(casePath.ref.individualId),
      kind: "case",
      route,
      title: "Case",
      ...(clientId ? { clientId } : {})
    };
  }
  const clientId = clientProfileId(pathname);
  if (clientId) {
    return {
      id: clientTabId(clientId),
      kind: "client",
      route,
      title: "Client",
      clientId
    };
  }
  const page = PAGE_TABS.find((p) => under(pathname, p.path));
  if (page) {
    return {
      id: `page:${page.id}`,
      kind: "page",
      route,
      title: page.title,
      pageId: page.id
    };
  }
  const label = defaultCrumbsForPath(pathname)[0]?.label;
  const root = pathname.split("/").filter(Boolean)[0] ?? "home";
  return {
    id: `page:${root}`,
    kind: "page",
    route,
    title: typeof label === "string" ? label : "Page",
    pageId: root
  };
}

/** The client a saved tab route belongs to, when the route names one. */
export const clientIdForRoute = (route: string): string | undefined => {
  const [pathname, query] = route.split("?");
  return tabForPath(pathname, query ? `?${query}` : "").clientId;
};

/** A fresh Home tab, opened when the last tab closes. */
export const homeTarget = (): TabTarget => ({
  id: HOME_TAB_ID,
  kind: "page",
  route: HOME_PATH,
  title: "Home",
  pageId: "home"
});
